import { PetrafiIcon } from '@repo/ui/components'
import { getApi } from '@/stores/api'
import { setSessionToken, setTenant } from '@/stores/session'
import { createFileRoute, Link } from '@tanstack/react-router'
import { LoaderCircleIcon, XCircleIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { Button } from '@repo/ui/components'
import { MESSAGE } from '@repo/constants'

const searchSchema = z.object({
  token: z.string().default(''),
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/magic-login')({
  validateSearch: searchSchema,
  component: MagicLoginPage,
})

function MagicLoginPage() {
  const { token, redirect } = Route.useSearch()
  const { t } = useTranslation('ui')
  const [error, setError] = useState<string | null>(null)
  const hasAttempted = useRef(false)

  useEffect(() => {
    if (!token) {
      setError(t('magicLogin.error.notFound'))
      return
    }

    // Guard against React StrictMode double-invoke consuming the one-time token
    if (hasAttempted.current) return
    hasAttempted.current = true

    const run = async () => {
      try {
        const api = getApi()
        const res = await api.public.onboarding['magic-login'].$post({ json: { token } })
        const data = await res.json()

        if ('sessionToken' in data && data.sessionToken && 'tenant' in data && data.tenant) {
          setSessionToken(data.sessionToken)
          setTenant(data.tenant as Parameters<typeof setTenant>[0])
          const tenant = data.tenant as { slug: string }
          const defaultTarget = `/${tenant.slug}/driver-onboarding/dashboard`
          const safeRedirect =
            redirect && redirect.startsWith('/') && !redirect.startsWith('//')
              ? redirect
              : defaultTarget
          window.location.href = safeRedirect
        } else {
          setError(t('magicLogin.error.generic'))
        }
      } catch (err: unknown) {
        // fetchWithThrow throws the JSON body on non-2xx; extract the message code
        const message =
          err && typeof err === 'object' && 'message' in err
            ? (err as { message: string }).message
            : null
        if (message === 'MAGIC_TOKEN_EXPIRED') {
          setError(t('magicLogin.error.expired'))
        } else if (message === 'MAGIC_TOKEN_ALREADY_USED') {
          setError(t('magicLogin.error.used'))
        } else if (message === 'MAGIC_TOKEN_NOT_FOUND') {
          setError(t('magicLogin.error.notFound'))
        } else if (message === MESSAGE.TENANT_INACTIVE) {
          setError(t('login.error.codes.TENANT_INACTIVE'))
        } else {
          setError(t('magicLogin.error.generic'))
        }
      }
    }

    run()
  }, [token, redirect, t])

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-6">
      <div className="flex items-center gap-2">
        <PetrafiIcon className="size-10 text-primary" />
        <span className="text-2xl font-bold">{t('driverPortal')}</span>
      </div>

      {!error ? (
        <div className="flex flex-col items-center gap-3">
          <LoaderCircleIcon className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">{t('magicLogin.loading')}</p>
        </div>
      ) : (
        <div className="flex w-full max-w-sm flex-col items-center gap-4 text-center">
          <div className="rounded-full bg-destructive/10 p-4">
            <XCircleIcon className="size-8 text-destructive" />
          </div>
          <p className="text-sm text-destructive">{error}</p>
          <Link to="/login">
            <Button variant="outline">{t('magicLogin.goToLogin')}</Button>
          </Link>
        </div>
      )}
    </div>
  )
}
